import { useCallback, useMemo } from 'react';
import { useAuth } from '@neotech-solutions-org/neofusion-fe-shared';
import useLocalStorage from './useLocalStorage';

const RECENT_SEARCHES_STORAGE_KEY = 'recentSearches';
const MAX_RECENT_SEARCHES = 5;

const EMPTY_SEARCHES: string[] = [];

type RecentSearchesByUserId = Record<string, string[]>;

const isSameSearchTerm = (searchTerm: string, comparedTerm: string): boolean =>
  searchTerm.toLowerCase() === comparedTerm.toLowerCase();

const getUpdatedRecentSearches = (currentRecentSearches: string[], searchTerm: string): string[] =>
  [searchTerm, ...currentRecentSearches.filter((term) => !isSameSearchTerm(term, searchTerm))].slice(
    0,
    MAX_RECENT_SEARCHES
  );

const useRecentSearches = () => {
  const { isAuthenticated, userId } = useAuth();

  const [storedRecentSearches, setStoredRecentSearches] = useLocalStorage<RecentSearchesByUserId>(
    RECENT_SEARCHES_STORAGE_KEY,
    {}
  );

  const recentSearches = useMemo(() => {
    if (isAuthenticated && userId) {
      return storedRecentSearches[userId] ?? EMPTY_SEARCHES;
    }
    return EMPTY_SEARCHES;
  }, [isAuthenticated, storedRecentSearches, userId]);

  const saveRecentSearch = useCallback(
    (searchTerm: string) => {
      const trimmedSearchTerm = searchTerm.trim();

      if (!isAuthenticated || !userId || !trimmedSearchTerm) return;

      setStoredRecentSearches((currentSearchesByUserId) => ({
        ...currentSearchesByUserId,
        [userId]: getUpdatedRecentSearches(currentSearchesByUserId[userId] ?? EMPTY_SEARCHES, trimmedSearchTerm),
      }));
    },
    [isAuthenticated, setStoredRecentSearches, userId]
  );

  const clearRecentSearches = useCallback(
    (targetUserId: string) => {
      if (!targetUserId) return;

      setStoredRecentSearches((currentSearchesByUserId) => {
        if (!currentSearchesByUserId[targetUserId]) {
          return currentSearchesByUserId;
        }

        const { [targetUserId]: _removed, ...remainingSearchesByUserId } = currentSearchesByUserId;

        return remainingSearchesByUserId;
      });
    },
    [setStoredRecentSearches]
  );

  return {
    recentSearches,
    saveRecentSearch,
    clearRecentSearches,
  };
};

export default useRecentSearches;
